import { Stack, Tooltip, Typography } from "@mui/material"
import { Position, useConnection, useReactFlow } from "@xyflow/react"
import classNames from "classnames"
import { useAtomValue, useSetAtom } from "jotai"
import { useCallback, useState } from "react"
import EdgeHandle from "@features/builder/graph/EdgeHandle"
import {
  AddNodeMode,
  addNodeModeAtom,
  nextDrawerNodeIdAtom,
  useGraph,
  useIsNodeHighlighted,
} from "@features/builder/graph/graphAtoms"
import { AppNode, NodeType, isNotGroupNode } from "@features/builder/graph/types"
import useNodeLock from "@features/builder/hooks/useNodeLock"
import { useNodesValidation } from "@features/builder/graph/nodes/validation/useNodesValidation"
import css from "./NodeContainer.module.css"
import { getParentsList } from "@utils/node/getParentsList"

type NodeContainerProps = {
  children: React.ReactElement
  node: AppNode
  hideConnector?: boolean
}

const NodeContainer = ({ children, node, hideConnector = false }: NodeContainerProps) => {
  const { getNode } = useReactFlow<AppNode>()
  const [isTooltipOpen, setIsTooltipOpen] = useState(false)
  const clsOutline = useIsNodeHighlighted(node.id)
  const addNodeMode = useAtomValue(addNodeModeAtom)
  const setNextDrawerNodeId = useSetAtom(nextDrawerNodeIdAtom)
  const { isNodeValid } = useNodesValidation()
  const { isLocked: isGraphLocked } = useGraph()
  const nodeLock = useNodeLock({ nodeId: node.id })

  const isGroup = !isNotGroupNode(node)
  const isEmpty = node.type === NodeType.Empty
  const haveLink = "metaNode" in node.data && node.data.metaNode.Link
  const haveParent = getParentsList(node).length
  const isError = isEmpty || !isNodeValid(node.id) || (haveLink && haveParent)

  const connection = useConnection<AppNode>()

  const handleClick = useCallback(() => {
    // group clicked in "adding mode" - do not open drawer for that node, new created node will be opened instead
    if (isGroup && addNodeMode !== AddNodeMode.None) return

    // prevent opening drawer for empty nodes when graph is locked as user won't be able to edit it nor close it
    if (isGraphLocked && isEmpty) return

    setNextDrawerNodeId(node.id)
  }, [addNodeMode, isEmpty, isGroup, isGraphLocked, node.id, setNextDrawerNodeId])

  return (
    <div
      style={{ height: "100%" }}
      className={classNames(
        clsOutline && css.highlightCollision,
        isError && css.highlightError,
        !!nodeLock && !connection.inProgress && css.grayscale,
      )}
      onClick={handleClick}
    >
      <Tooltip
        arrow
        followCursor
        componentsProps={{ tooltip: { sx: { maxWidth: "none" } } }}
        open={isTooltipOpen}
        onClose={() => setIsTooltipOpen(false)}
        onOpen={() => setIsTooltipOpen(true)}
        title={
          isTooltipOpen && (
            <Stack alignItems="center">
              {node.parentId && (
                <Typography sx={{ fontSize: 12 }}>
                  Parent Group: {getNode(node.parentId)?.data?.label}
                </Typography>
              )}
              <Typography sx={{ fontWeight: "bold" }}>{node.data?.label}</Typography>
              {isEmpty && <Typography>Empty node cannot be sampled</Typography>}

              {isGroup && (
                <Typography variant="caption">
                  Double click to {node.data.metaGroup.isCollapsed ? "expand" : "collapse"}
                </Typography>
              )}
            </Stack>
          )
        }
      >
        <span className={css.contentWrapper}>
          {children}
          {!hideConnector && (
            <>
              {/* order matters as we render both handles in the same place, make sure "source" handle is always on top */}
              <EdgeHandle
                node={node}
                type="target"
                data-testid="node-connector-target"
                position={node.targetPosition || Position.Bottom}
              />
              <EdgeHandle
                node={node}
                type="source"
                data-testid="node-connector-source"
                position={node.sourcePosition || Position.Bottom}
              />
            </>
          )}
        </span>
      </Tooltip>
    </div>
  )
}

export default NodeContainer
